import type { PlainMessage } from '@bufbuild/protobuf';

import {
  NoteTableCellType,
  type NoteTableBlock,
  type NoteTableColumn,
  type NoteTableRowValue,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import {
  DocumentAnswerSource,
  type NoteAnswerTableRow,
  type NoteToothNotation,
} from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';

import { printNoteDate } from './noteDate';
import { printNoteNumber } from './noteNumbers';
import { formatNoteTooth, isWaitingAiHint } from './noteView';
import { joinProtocolValues } from './templateDraft';

// A table field: a grid of typed cells,
// rows the template fixes or the note adds. Its value holds every row with
// a cell for every column, an empty or unticked one too, as scribe stores
// it (`note_table.go`).

export type NoteTableRows = PlainMessage<NoteTableRowValue>[];

type TableDef = PlainMessage<NoteTableBlock>;
type Column = PlainMessage<NoteTableColumn>;

// A cell nobody filled: "false" for a checkbox, else empty.
export const emptyNoteTableCell = (column: Pick<Column, 'Type'>) =>
  column.Type === NoteTableCellType.NoteTableCellTypeCheckbox ? 'false' : '';

// A cell that holds something: a ticked box, or any other value.
export const isNoteTableCellFilled = (value: string) =>
  value !== '' && value !== 'false';

const holdsSomething = (row: PlainMessage<NoteTableRowValue>) =>
  row.Cells.some((cell) => isNoteTableCellFilled(cell.Value));

// The rows the grid shows, each with a cell for every column in column
// order: a fixed table's rows in the template's order, a table whose rows
// the note adds as its value holds them.
export const listNoteTableRows = (
  def: TableDef,
  rows: NoteTableRows,
): NoteTableRows => {
  const cellsOf = (row: PlainMessage<NoteTableRowValue> | undefined) =>
    def.Columns.map((column) => ({
      Column: column.Code,
      Value:
        row?.Cells.find((cell) => cell.Column === column.Code)?.Value ??
        emptyNoteTableCell(column),
    }));
  if (def.AddRows) {
    return rows.map((row) => ({ Row: row.Row, Cells: cellsOf(row) }));
  }
  return def.Rows.map((fixed) => ({
    Row: fixed.Code,
    Cells: cellsOf(rows.find((row) => row.Row === fixed.Code)),
  }));
};

// The rows with one cell set.
export const setNoteTableCell = (
  rows: NoteTableRows,
  rowIndex: number,
  column: string,
  value: string,
): NoteTableRows =>
  rows.map((row, index) =>
    index === rowIndex
      ? {
          ...row,
          Cells: row.Cells.map((cell) =>
            cell.Column === column ? { ...cell, Value: value } : cell,
          ),
        }
      : row,
  );

// A row the clinician adds: empty, without an id until scribe mints one.
export const addNoteTableRow = (
  def: TableDef,
  rows: NoteTableRows,
): NoteTableRows => [
  ...rows,
  ...listNoteTableRows(def, [{ Row: '', Cells: [] }]),
];

// What a change of the table sends, the whole table: scribe drops an added
// row with nothing in it, so such a row is not sent; null when no cell
// holds anything, which clears the field.
export const toNoteTableChange = (
  def: TableDef,
  rows: NoteTableRows,
): NoteTableRows | null => {
  if (!rows.some(holdsSomething)) {
    return null;
  }
  return def.AddRows ? rows.filter(holdsSomething) : rows;
};

// A cell as the note prints it after its column's heading: a date in the
// template language's format, a number with its unit, a tooth in the note's
// notation.
export const printNoteTableCell = (
  column: Column,
  value: string,
  { language, notation }: { language?: string; notation?: NoteToothNotation },
) => {
  switch (column.Type) {
    case NoteTableCellType.NoteTableCellTypeDate:
      return printNoteDate(value, language);
    case NoteTableCellType.NoteTableCellTypeNumber:
      return printNoteNumber(value, column.Unit, language);
    case NoteTableCellType.NoteTableCellTypeTooth:
      return formatNoteTooth(value, notation);
    default:
      return value;
  }
};

// The table as scribe prints it (`note_language.go` tableText): row by row,
// a fixed row as its heading, ": " and its cells, an added row as its cells
// alone, rows joined by "; "; a ticked box as its column's heading, any
// other cell that holds something as the heading and its value. An
// unticked box, an empty cell and a row with neither print nothing.
export const printNoteTable = (
  def: TableDef,
  rows: NoteTableRows,
  options: { language?: string; notation?: NoteToothNotation },
) =>
  rows
    .flatMap((row) => {
      const cells = row.Cells.flatMap((cell) => {
        const column = def.Columns.find((item) => item.Code === cell.Column);
        if (!column || !isNoteTableCellFilled(cell.Value)) {
          return [];
        }
        return column.Type === NoteTableCellType.NoteTableCellTypeCheckbox
          ? [column.Label]
          : [
              `${column.Label} ${printNoteTableCell(column, cell.Value, options)}`,
            ];
      });
      if (!cells.length) {
        return [];
      }
      const text = joinProtocolValues(cells);
      const label = def.Rows.find((fixed) => fixed.Code === row.Row)?.Label;
      return [label ? `${label}: ${text}` : text];
    })
    .join('; ');

// The key of one cell, its row and its column.
export const noteTableCellKey = (row: string, column: string) =>
  `${row}/${column}`;

export type NoteTableAiCell = { unconfirmed: boolean; quote: string };

// The cells AI set, by noteTableCellKey: whether each waits for the
// clinician's confirmation (assumed or suggested) and the words it was read
// from.
export const listNoteTableAiCells = (
  rows: PlainMessage<NoteAnswerTableRow>[],
): Record<string, NoteTableAiCell> =>
  Object.fromEntries(
    rows.flatMap((row) =>
      row.Cells.flatMap((cell) =>
        cell.Source === DocumentAnswerSource.DocumentAnswerSourceAI
          ? [
              [
                noteTableCellKey(row.Row, cell.Column),
                {
                  unconfirmed: isWaitingAiHint(cell),
                  quote: cell.EvidenceQuote,
                },
              ],
            ]
          : [],
      ),
    ),
  );
